/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import i18next from 'i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import { DateTime } from 'luxon';
import { initReactI18next } from 'react-i18next';

import { DefaultLanguage, Languages } from 'shared';

import { deTranslation } from './locales/deTranslation';
import { enTranslation } from './locales/enTranslation';
import { createBundle } from './utils/createBundle';
import { currency, currencyShort } from './utils/currencyFormaters';
import { uiLanguage } from './utils/storageHelpers';

export enum Namespaces {
  About = 'about',
  Admin = 'admin',
  Cluster = 'cluster',
  Common = 'common',
  ConfirmationPage = 'confirmationPage',
  Countries = 'countries',
  CreateCluster = 'createCluster',
  CreateOrganization = 'createOrganization',
  CreateProject = 'createProject',
  Entities = 'entities',
  General = 'general',
  Login = 'login',
  Navigation = 'navigation',
  OrganizationAdmin = 'organizationAdmin',
  Project = 'project',
  Rbac = 'rbac',
  Setup = 'setup',
  UserProfile = 'userProfile',
  Validation = 'validation',
}

const createI18nInstance = ({ language = DefaultLanguage }: { language?: string }) => {
  const instance = i18next.createInstance();
  // "void" prevents unhandled promise warnings from i18n
  void instance
    .use(I18nextBrowserLanguageDetector)
    .use(initReactI18next)
    .init({
      lng: language,
      ns: Object.values(Namespaces),
      preload: Object.values(Languages),
      resources: {
        en: createBundle(Namespaces, enTranslation),
        de: createBundle(Namespaces, deTranslation),
      },
      react: {
        wait: true,
        useSuspense: false,
        bindI18n: 'languageChanged',
      },
      interpolation: {
        escapeValue: false,
        format: function (value, format, lng, opts) {
          if ((format === 'currency' || format === 'currencyShort') && !isNaN(value) && opts?.currency) {
            switch (format) {
              case 'currency':
                return currency({ value: +value, currency: opts.currency, language: lng });
              case 'currencyShort':
                return currencyShort({ value: +value, currency: opts.currency, language: lng });
            }
          }
          if (format && format.indexOf('$') === 0) {
            const m = DateTime.fromISO(value);
            if (lng) {
              m.setLocale(lng);
            }
            const mask = format.slice(1);
            if (mask === 'from_now') {
              return m.toRelative();
            } else {
              return m.toFormat(mask);
            }
          }
          if (format === 'fromNow') {
            const m = DateTime.fromISO(value);
            if (lng) {
              m.setLocale(lng);
            }
            return m.toRelative();
          }
          if (format === 'uppercase') {
            return value.toUpperCase();
          }
          if (format === 'lowercase') {
            return value.toLowerCase();
          }
          return value;
        },
        formatSeparator: '|',
      },
    });

  return instance;
};

export const i18n = createI18nInstance({ language: uiLanguage.get() });
